import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";
import { api } from "../../api/apiService";
import { Spinner } from "react-bootstrap";

const PrintPayrollView = () => {
    const [searchParams] = useSearchParams();
    const id = searchParams.get("id");
    const departmentId = searchParams.get("departmentId");
    const company = localStorage.getItem("company");
    const [payrollData, setPayrollData] = useState(null);
    const [isLoading, setIsLoading] = useState(true);

    useEffect(() => {
        const fetchData = async () => {
            try {
                setIsLoading(true);
                const response = await api.get(`/payrolls/${id}/details?department_id=${departmentId}`);
                setPayrollData(response.data);
                setIsLoading(false);
                setTimeout(() => {
                    window.print();
                    window.addEventListener("afterprint", handleAfterPrint);
                }, 1000);
            } catch (error) {
                setIsLoading(false);
                toast.error("Failed to fetch job data");
            }
        };

        fetchData();
        return () => {
            window.removeEventListener("afterprint", handleAfterPrint);
        };
    }, []);

    const handleAfterPrint = () => {
        window.close();
    };

    // Calculate totals for each column
    const calculateTotals = () => {
        const totals = {
            actual_days: 0,
            extra: 0,
            calc_days: 0,
            pay_rate: 0,
            bonus: 0,
            grand_total: 0,
        };

        payrollData?.payroll_entries?.forEach(entry => {
            totals.actual_days += Number(entry.actual_days) || 0;
            totals.extra += Number(entry.extra) || 0;
            totals.calc_days += Number(entry.calc_days) || 0;
            totals.pay_rate += Number(entry.pay_rate) || 0;
            totals.bonus += Number(entry.bonus) || 0;
            totals.grand_total += Number(entry.grand_total) || 0;
        });

        totals.actual_days = Math.round(totals.actual_days * 10) / 10;
        totals.extra = Math.round(totals.extra * 10) / 10;
        totals.calc_days = Math.round(totals.calc_days * 10) / 10;
        totals.pay_rate = Math.round(totals.pay_rate * 10) / 10;
        totals.bonus = Math.round(totals.bonus * 10) / 10;
        totals.grand_total = Math.round(totals.grand_total * 10) / 10;

        return totals;
    };
    const totals = payrollData ? calculateTotals() : null;

    const formatNumber = (value) => {
        const roundedValue = Math.round(value * 10) / 10;
        return new Intl.NumberFormat('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(roundedValue);
    };

    return (
        <>
            <style>
                {`
                    @media print {
                        body {
                            margin: 0 !important;
                            padding: 0 !important;
                        }
                        .main {
                            margin: 0 !important;
                            padding: 0 !important;
                        }
                        .section {
                            margin: 0 !important;
                            padding: 0 !important;
                        }
                        .table {
                            margin: 0 !important;        
                            padding: 0 !important;
                        }
                    }
                `}
            </style>
            <main className="main m-5">
                <section className="section">
                    <div className="row">
                        <div className="col-lg-12">
                            <div className="card">
                                <div className="card-body">
                                    <h5 className="card-title mb-0">Payroll - {payrollData?.title}</h5>
                                    {isLoading ? (
                                        <div className="text-center mt-3">
                                            <Spinner animation="border" variant="primary" />
                                            <span className="ms-2">Loading...</span>
                                        </div>
                                    ) : payrollData?.payroll_entries?.length > 0 ? (
                                        <div className="table-responsive mb-5">
                                            <table className="table">
                                                <thead>
                                                    <tr>
                                                        <th width="180">Name</th>
                                                        <th width="130">Actual Days</th>
                                                        <th width="130">Extra Days</th>
                                                        <th width="130">Total Days</th>
                                                        <th className="text-center" width="130">Pay Rate</th>
                                                        <th className="text-center" width="130">Bonus</th>
                                                        <th width="130" className="text-center">Grand Total</th>
                                                        <th width="130" className="text-end">Signature</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {payrollData?.payroll_entries.map((item, index) => (
                                                        <tr key={item.employee_id}>
                                                            <td>{item?.employee?.first_name} {item?.employee?.last_name}
                                                                <p className="m-0 small">({item?.employee.role?.role_name})</p>
                                                            </td>
                                                            <td>{item.actual_days}</td>
                                                            <td>{item.extra}</td>
                                                            <td>{item.calc_days}</td>
                                                            <td className="text-end">{formatNumber(item.pay_rate)}</td>
                                                            <td className="text-end">{formatNumber(item.bonus)}</td>
                                                            <td className="text-end">{formatNumber(item.grand_total)}</td>
                                                            <td className="text-end" style={{ verticalAlign: 'bottom' }}>
                                                                <div className="ml-auto w-75 border-dark border-bottom"></div>
                                                            </td>
                                                        </tr>
                                                    ))}
                                                    {/* Add the totals row */}
                                                    <tr>
                                                        <td className="text-end"><strong>Total:</strong></td>
                                                        <td><strong>{totals.actual_days}</strong></td>
                                                        <td><strong>{totals.extra}</strong></td>
                                                        <td><strong>{totals.calc_days}</strong></td>
                                                        <td className="text-end"><strong>{formatNumber(totals.pay_rate)}</strong></td>
                                                        <td className="text-end"><strong>{formatNumber(totals.bonus)}</strong></td>
                                                        <td className="text-end"><strong>{formatNumber(totals.grand_total)}</strong></td>
                                                        <td className="text-end"></td>
                                                    </tr>
                                                </tbody>
                                            </table>
                                        </div>
                                    ) : (
                                        <p>No payroll data available.</p>
                                    )}
                                    {!isLoading && <h5>{company}</h5>}
                                </div>
                            </div>
                        </div>
                    </div>
                </section>
            </main>
        </>
    );
};

export default PrintPayrollView;

